import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import GroupIcon from '@mui/icons-material/Group';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { useTranslation } from 'react-i18next';
import {
  ensureDirectConversation,
  ensureFriendsConversation,
  ensureNightConversation,
  fetchChatConversation,
  fetchChatMessages,
  markChatConversationRead,
  sendChatMessage,
  toggleChatMessageReaction,
} from '../../api/chat';
import PresenceBadge from '../companions/PresenceBadge';
import { getInitials } from '../companions/presence';
import { setActiveChatConversationId } from './ChatNotificationBridge';

const typeLabelKey = {
  night: 'chat.types.night',
  direct: 'chat.types.direct',
  friends: 'chat.types.group',
  group: 'chat.types.group',
};

const conversationTitle = (conversation, fallbackTitle) => {
  if (!conversation) return fallbackTitle;
  if (conversation.type === 'direct') {
    const other = (conversation.participants || []).find(
      (participant) => participant.id !== conversation.viewerId
    );

    return other?.displayName || fallbackTitle;
  }

  return conversation.title || fallbackTitle;
};

const quickReactionEmojis = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
const CHAT_IMAGE_MAX_SIDE = 1280;
const CHAT_IMAGE_QUALITY = 0.78;
const REPLY_SWIPE_THRESHOLD = 64;
const REPLY_SWIPE_MAX_OFFSET = 74;

const giphyApiKey = process.env.REACT_APP_GIPHY_API_KEY || '';

const isGifUrl = (body = '') => /^https:\/\/media\d*\.giphy\.com\/media\/.+\.gif/i.test(body.trim());

const messageSnippet = (message) => {
  if (message?.metadata?.attachment?.type === 'image') return 'Photo';

  const body = message?.body || message?.metadata?.replyTo?.bodyPreview || '';
  const normalized = isGifUrl(body) ? 'GIF' : String(body).replace(/\s+/g, ' ').trim();
  return normalized.length > 90 ? `${normalized.slice(0, 87)}...` : normalized;
};

const presenceDotColor = (presence) => {
  if (presence?.isOnline) return 'success.main';
  if (presence?.lastSeenAt) return 'secondary.main';
  return 'text.disabled';
};

const fileToImageAttachment = (file) =>
  new Promise((resolve, reject) => {
    if (!file?.type?.startsWith('image/')) {
      reject(new Error('Unsupported image'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Unable to read image'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('Unable to load image'));
      image.onload = () => {
        const scale = Math.min(1, CHAT_IMAGE_MAX_SIDE / Math.max(image.width, image.height));
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')?.drawImage(image, 0, 0, width, height);

        const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        resolve({
          type: 'image',
          url: canvas.toDataURL(mimeType, CHAT_IMAGE_QUALITY),
          mimeType,
          fileName: file.name || 'photo.jpg',
          size: file.size || 0,
        });
      };
      image.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
  });

const TavernChatPanel = ({ context = {}, onClose, open }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const messagesEndRef = useRef(null);
  const giphyRef = useRef(null);
  const swipeStartRef = useRef(null);
  const documentInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const cameraPhotoInputRef = useRef(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [composerAnchor, setComposerAnchor] = useState(null);
  const [composerMode, setComposerMode] = useState(null);
  const [reactionAnchor, setReactionAnchor] = useState(null);
  const [reactionMessage, setReactionMessage] = useState(null);
  const [gifQuery, setGifQuery] = useState('board game');
  const [gifs, setGifs] = useState([]);
  const [gifLoading, setGifLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorKey, setErrorKey] = useState('');
  const [replySwipe, setReplySwipe] = useState(null);
  const type = conversation?.type || context.type || 'group';
  const participants = useMemo(
    () =>
      conversation?.participants?.length
        ? conversation.participants
        : context.participants || [],
    [context.participants, conversation?.participants]
  );
  const title = conversationTitle(conversation, context.title || t('chat.title'));
  const canSend = Boolean(conversation?.id) && draft.trim() && !sending;
  const directCompanion = conversation?.type === 'direct'
    ? (conversation.participants || []).find((participant) => participant.id !== conversation.viewerId)
    : null;
  const presenceByUserId = useMemo(() => {
    const next = new Map();
    participants.forEach((participant) => {
      next.set(Number(participant.id), participant.presence || null);
    });
    return next;
  }, [participants]);

  const scrollToLatestMessage = useCallback((behavior = 'smooth') => {
    const scroll = () => {
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    };

    scroll();
    window.requestAnimationFrame?.(scroll);
    window.setTimeout(scroll, 80);
    window.setTimeout(scroll, 240);
  }, []);

  const openConversation = useCallback(async () => {
    if (!open) return;

    setLoading(true);
    setErrorKey('');
    setConversation(null);
    setMessages([]);
    setReplyToMessage(null);

    try {
      let response;

      if (context.conversationId) {
        response = await fetchChatConversation(context.conversationId);
      } else if (context.type === 'night') {
        response = await ensureNightConversation(context.nightId);
      } else if (context.type === 'direct') {
        response = await ensureDirectConversation(context.userId || context.participants?.[0]?.id);
      } else {
        response = await ensureFriendsConversation();
      }

      const nextConversation = response.conversation;
      setConversation(nextConversation);

      const messageData = await fetchChatMessages(nextConversation.id);
      setMessages(messageData.items || []);
      await markChatConversationRead(nextConversation.id).catch(() => {});
    } catch {
      setErrorKey('chat.feedback.loadError');
    } finally {
      setLoading(false);
    }
  }, [context.conversationId, context.nightId, context.participants, context.type, context.userId, open]);

  useEffect(() => {
    openConversation();
  }, [openConversation]);

  useEffect(() => {
    if (!open) {
      setDraft('');
      setReplyToMessage(null);
      setComposerAnchor(null);
      setComposerMode(null);
      setReactionAnchor(null);
      setReactionMessage(null);
    }
  }, [open]);

  useEffect(() => {
    if (composerMode !== 'gif' || !giphyApiKey) return undefined;

    let active = true;
    const searchGifs = async () => {
      setGifLoading(true);

      try {
        if (!giphyRef.current) {
          const { GiphyFetch } = await import('@giphy/js-fetch-api');
          giphyRef.current = new GiphyFetch(giphyApiKey);
        }

        const response = await giphyRef.current.search(gifQuery || 'board game', {
          limit: 12,
          rating: 'g',
        });
        if (active) setGifs(response.data || []);
      } catch {
        if (active) setGifs([]);
      } finally {
        if (active) setGifLoading(false);
      }
    };

    searchGifs();

    return () => {
      active = false;
    };
  }, [composerMode, gifQuery]);

  useEffect(() => {
    if (!open) return;
    scrollToLatestMessage('smooth');
  }, [messages.length, open, scrollToLatestMessage]);

  useEffect(() => {
    if (!open || !context.scrollToBottomToken || loading) return;
    scrollToLatestMessage('auto');
  }, [context.scrollToBottomToken, loading, open, scrollToLatestMessage]);

  useEffect(() => {
    if (!conversation?.id) return undefined;

    if (open) {
      setActiveChatConversationId(conversation.id);
    }

    const handleServerEvent = async (event) => {
      if (event.detail?.type === 'presence.updated') {
        setConversation((current) => {
          if (!current?.participants?.length) return current;

          return {
            ...current,
            participants: current.participants.map((participant) =>
              Number(participant.id) === Number(event.detail.userId)
                ? { ...participant, presence: event.detail.presence }
                : participant
            ),
          };
        });
        return;
      }

      if (
        !['chat.message.created', 'chat.message.updated'].includes(event.detail?.type) ||
        Number(event.detail.conversationId) !== Number(conversation.id)
      ) {
        return;
      }

      try {
        const messageData = await fetchChatMessages(conversation.id);
        setMessages(messageData.items || []);
        await markChatConversationRead(conversation.id).catch(() => {});
      } catch {
        setErrorKey('chat.feedback.loadError');
      }
    };

    window.addEventListener('thetavern:server-event', handleServerEvent);

    return () => {
      setActiveChatConversationId(null);
      window.removeEventListener('thetavern:server-event', handleServerEvent);
    };
  }, [conversation?.id, open]);

  const submitMessage = async (event) => {
    event?.preventDefault();
    if (!canSend) return;

    const body = draft.trim();
    setSending(true);
    setErrorKey('');

    try {
      const response = await sendChatMessage(conversation.id, body, {
        replyToMessageId: replyToMessage?.id,
      });
      setDraft('');
      setReplyToMessage(null);
      setMessages((current) => {
        if (current.some((message) => message.id === response.message.id)) return current;
        return [...current, response.message];
      });
      if (response.conversation) setConversation(response.conversation);
    } catch {
      setErrorKey('chat.feedback.sendError');
    } finally {
      setSending(false);
    }
  };

  const sendBody = async (body) => {
    if (!conversation?.id || sending || !body?.trim()) return;

    setSending(true);
    setErrorKey('');

    try {
      const response = await sendChatMessage(conversation.id, body.trim(), {
        replyToMessageId: replyToMessage?.id,
      });
      setDraft('');
      setComposerAnchor(null);
      setComposerMode(null);
      setReplyToMessage(null);
      setMessages((current) => {
        if (current.some((message) => message.id === response.message.id)) return current;
        return [...current, response.message];
      });
      if (response.conversation) setConversation(response.conversation);
    } catch {
      setErrorKey('chat.feedback.sendError');
    } finally {
      setSending(false);
    }
  };

  const sendImageFile = async (file) => {
    if (!conversation?.id || sending || !file) return;

    if (!file.type?.startsWith('image/')) {
      appendAttachmentDraft(t('chat.attachments.photoTemplate'), [file]);
      return;
    }

    setSending(true);
    setErrorKey('');

    try {
      const attachment = await fileToImageAttachment(file);
      const response = await sendChatMessage(conversation.id, t('chat.attachments.photoMessage'), {
        attachment,
        replyToMessageId: replyToMessage?.id,
      });
      setDraft('');
      setComposerAnchor(null);
      setComposerMode(null);
      setReplyToMessage(null);
      setMessages((current) => {
        if (current.some((message) => message.id === response.message.id)) return current;
        return [...current, response.message];
      });
      if (response.conversation) setConversation(response.conversation);
    } catch {
      setErrorKey('chat.feedback.sendError');
    } finally {
      setSending(false);
    }
  };

  const appendAttachmentDraft = (template, files) => {
    const names = Array.from(files || [])
      .map((file) => file.name)
      .filter(Boolean)
      .join(', ');

    if (!names) return;

    setDraft((current) => {
      const separator = current && !current.endsWith('\n') ? '\n' : '';
      return `${current}${separator}${template}${names}`;
    });
    setComposerAnchor(null);
    setComposerMode(null);
  };

  const openFilePicker = (inputRef) => {
    setComposerAnchor(null);
    setComposerMode(null);
    inputRef.current?.click();
  };

  const openComposerTool = (event, mode) => {
    setComposerAnchor(event.currentTarget);
    setComposerMode((current) => (current === mode ? null : mode));
  };

  const openReactionPicker = (event, message) => {
    setReactionAnchor(event.currentTarget);
    setReactionMessage(message);
  };

  const closeReactionPicker = () => {
    setReactionAnchor(null);
    setReactionMessage(null);
  };

  const startReplyToMessage = (message) => {
    setReplyToMessage({
      id: message.id,
      bodyPreview: messageSnippet(message),
      sender: message.sender,
    });
    setComposerAnchor(null);
    setComposerMode(null);
    setReactionAnchor(null);
    setReactionMessage(null);
  };

  const handleMessagePointerDown = (event, message) => {
    if (event.button !== undefined && event.button !== 0) return;
    swipeStartRef.current = {
      message,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setReplySwipe({
      active: true,
      messageId: message.id,
      offset: 0,
      ready: false,
    });
  };

  const handleMessagePointerMove = (event) => {
    const start = swipeStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    const horizontalIntent = Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY);

    if (!horizontalIntent && Math.abs(deltaX) < REPLY_SWIPE_THRESHOLD) {
      return;
    }

    const offset =
      Math.sign(deltaX || 1) *
      Math.min(REPLY_SWIPE_MAX_OFFSET, Math.max(0, Math.abs(deltaX) * 0.82));

    setReplySwipe({
      active: true,
      messageId: start.message.id,
      offset,
      ready: Math.abs(deltaX) >= REPLY_SWIPE_THRESHOLD && Math.abs(deltaY) <= 42,
    });
  };

  const handleMessagePointerUp = (event) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setReplySwipe(null);
    if (!start) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    if (Math.abs(deltaX) >= REPLY_SWIPE_THRESHOLD && Math.abs(deltaY) <= 42) {
      startReplyToMessage(start.message);
    }
  };

  const cancelReplySwipe = (event) => {
    swipeStartRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setReplySwipe(null);
  };

  const reactToMessage = async (emoji) => {
    if (!conversation?.id || !reactionMessage?.id) return;

    const messageId = reactionMessage.id;
    try {
      const response = await toggleChatMessageReaction(conversation.id, messageId, emoji);
      setMessages((current) =>
        current.map((message) =>
          Number(message.id) === Number(messageId)
            ? { ...message, reactions: response.reactions || [] }
            : message
        )
      );
    } catch {
      setErrorKey('chat.feedback.sendError');
    } finally {
      closeReactionPicker();
    }
  };

  const participantLabel = useMemo(() => {
    if (!participants.length) return t('chat.participants', { count: 1 });
    return t('chat.participants', { count: participants.length });
  }, [participants.length, t]);

  return (
    <Drawer
      anchor={mobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 430 },
          maxWidth: '100%',
          height: { xs: 'min(86vh, 720px)', sm: '100%' },
          borderTopLeftRadius: { xs: 28, sm: 0 },
          borderTopRightRadius: { xs: 28, sm: 0 },
          overflow: 'hidden',
          bgcolor: 'background.paper',
          backgroundImage:
            'radial-gradient(circle at 18% 4%, rgba(236, 170, 65, 0.18), transparent 18rem), linear-gradient(180deg, rgba(255,250,239,0.98), rgba(245,241,230,0.95))',
        },
      }}
    >
      <Stack sx={{ height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            pt: { xs: 2.5, sm: 3 },
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(255, 250, 239, 0.72)',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                flex: '0 0 auto',
                color: 'secondary.light',
                bgcolor: 'primary.main',
                boxShadow: '0 14px 28px -20px rgba(6, 37, 34, 0.72)',
              }}
            >
              {loading ? (
                <CircularProgress color="inherit" size={20} />
              ) : directCompanion?.avatarUrl ? (
                <Avatar src={directCompanion.avatarUrl} sx={{ width: 46, height: 46 }} />
              ) : type === 'direct' ? (
                getInitials(title)
              ) : type === 'night' ? (
                <GroupIcon />
              ) : (
                <LocalFireDepartmentIcon />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip
                  size="small"
                  label={t(typeLabelKey[type] || typeLabelKey.group)}
                  sx={{
                    height: 22,
                    bgcolor: 'rgba(15, 76, 69, 0.1)',
                    color: 'primary.dark',
                    fontWeight: 900,
                  }}
                />
                <Chip
                  size="small"
                  icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={t('chat.private')}
                  sx={{
                    height: 22,
                    bgcolor: 'rgba(236, 170, 65, 0.18)',
                    color: 'secondary.dark',
                    fontWeight: 900,
                  }}
                />
              </Stack>
              <Typography variant="h3" sx={{ overflowWrap: 'anywhere' }}>
                {title}
              </Typography>
              {directCompanion?.presence ? (
                <Box sx={{ mt: 0.75 }}>
                  <PresenceBadge presence={directCompanion.presence} size="small" noWrap />
                </Box>
              ) : null}
            </Box>
            <IconButton onClick={onClose} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            px: { xs: 2.5, sm: 3 },
            py: 2.5,
          }}
        >
          <Stack spacing={2.25}>
            {errorKey ? <Alert severity="error">{t(errorKey)}</Alert> : null}

            {participants.length > 0 ? (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                {participants.slice(0, 8).map((participant) => (
                  <Box key={participant.id || participant.displayName || participant.username}>
                    <Avatar
                      src={participant.avatarUrl || undefined}
                      alt={participant.displayName || participant.username || t('common.unknown')}
                      sx={{
                        width: 34,
                        height: 34,
                        border: '2px solid',
                        borderColor: 'background.paper',
                        boxShadow: '0 8px 18px -16px rgba(6, 37, 34, 0.7)',
                      }}
                    >
                      {getInitials(participant.displayName || participant.username)}
                    </Avatar>
                    {participant.presence?.isOnline ? (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          border: '2px solid',
                          borderColor: 'background.paper',
                          mt: -1.1,
                          ml: 2.55,
                          position: 'relative',
                        }}
                      />
                    ) : null}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary" sx={{ pl: 0.75 }}>
                  {participantLabel}
                </Typography>
              </Stack>
            ) : null}

            {loading ? (
              <Stack spacing={1.25} alignItems="center" sx={{ py: 5 }}>
                <CircularProgress size={24} />
                <Typography color="text.secondary">{t('chat.feedback.loading')}</Typography>
              </Stack>
            ) : messages.length === 0 ? (
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'rgba(15, 76, 69, 0.16)',
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                }}
              >
                <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
                  {t('chat.empty.title')}
                </Typography>
                <Typography color="text.secondary">{t('chat.empty.body')}</Typography>
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {messages.map((message) => {
                  const mine = message.senderId === conversation?.viewerId;
                  const senderPresence = presenceByUserId.get(Number(message.senderId));
                  const quotedMessage = message.metadata?.replyTo;
                  const imageAttachment =
                    message.metadata?.attachment?.type === 'image' ? message.metadata.attachment : null;
                  const activeReplySwipe = replySwipe?.messageId === message.id ? replySwipe : null;
                  const replySwipeOffset = activeReplySwipe?.offset || 0;
                  const replySwipeDirection = replySwipeOffset < 0 ? 'left' : 'right';
                  const replySwipeProgress = Math.min(1, Math.abs(replySwipeOffset) / REPLY_SWIPE_THRESHOLD);

                  return (
                    <Box
                      key={message.id}
                      sx={{ position: 'relative' }}
                    >
                    {activeReplySwipe ? (
                      <Box
                        aria-hidden="true"
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          [replySwipeDirection === 'left' ? 'right' : 'left']: 18,
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          color: activeReplySwipe.ready ? 'primary.contrastText' : 'primary.main',
                          bgcolor: activeReplySwipe.ready
                            ? 'primary.main'
                            : alpha(theme.palette.primary.main, 0.12),
                          opacity: 0.25 + replySwipeProgress * 0.75,
                          transform: `translateY(-50%) scale(${0.82 + replySwipeProgress * 0.18})`,
                          transition: 'background-color 120ms ease, color 120ms ease, opacity 120ms ease',
                          zIndex: 0,
                        }}
                      >
                        <ReplyRoundedIcon fontSize="small" />
                      </Box>
                    ) : null}
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-end"
                      justifyContent={mine ? 'flex-end' : 'flex-start'}
                      onPointerDown={(event) => handleMessagePointerDown(event, message)}
                      onPointerMove={handleMessagePointerMove}
                      onPointerUp={handleMessagePointerUp}
                      onPointerCancel={cancelReplySwipe}
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        touchAction: 'pan-y',
                        transform: `translateX(${replySwipeOffset}px)`,
                        transition: activeReplySwipe?.active ? 'none' : 'transform 180ms ease-out',
                        willChange: activeReplySwipe ? 'transform' : 'auto',
                      }}
                    >
                      {mine ? (
                        <Stack direction="row" spacing={0.25} sx={{ alignSelf: 'center' }}>
                          <IconButton
                            type="button"
                            aria-label={t('chat.reply.action')}
                            size="small"
                            onClick={() => startReplyToMessage(message)}
                            sx={{
                              color: 'text.secondary',
                              opacity: 0.72,
                              '&:hover': { opacity: 1 },
                            }}
                          >
                            <ReplyRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            type="button"
                            aria-label={t('chat.reactions.add')}
                            size="small"
                            onClick={(event) => openReactionPicker(event, message)}
                            sx={{
                              color: 'text.secondary',
                              opacity: 0.72,
                              '&:hover': { opacity: 1 },
                            }}
                          >
                            <EmojiEmotionsOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : null}
                      {!mine ? (
                        <Box sx={{ position: 'relative', flex: '0 0 auto' }}>
                          <Avatar
                            src={message.sender?.avatarUrl || undefined}
                            alt={message.sender?.displayName || ''}
                            sx={{ width: 28, height: 28 }}
                          >
                            {getInitials(message.sender?.displayName)}
                          </Avatar>
                          <Box
                            aria-hidden="true"
                            sx={{
                              position: 'absolute',
                              right: -1,
                              bottom: -1,
                              width: 9,
                              height: 9,
                              borderRadius: '50%',
                              bgcolor: presenceDotColor(senderPresence),
                              border: '2px solid',
                              borderColor: 'background.paper',
                              boxShadow: senderPresence?.isOnline
                                ? '0 0 0 3px rgba(47, 138, 86, 0.12)'
                                : 'none',
                            }}
                          />
                        </Box>
                      ) : null}
                      <Stack spacing={0.4} alignItems={mine ? 'flex-end' : 'flex-start'} sx={{ maxWidth: '82%' }}>
                        <Box
                          sx={{
                            px: 1.65,
                            py: 1.25,
                            borderRadius: 3,
                            borderTopLeftRadius: mine ? 18 : 6,
                            borderTopRightRadius: mine ? 6 : 18,
                            color: mine ? 'primary.contrastText' : 'text.primary',
                            bgcolor: mine ? 'primary.main' : 'rgba(255, 250, 239, 0.92)',
                            border: mine ? 0 : '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 12px 26px -22px rgba(6, 37, 34, 0.52)',
                          }}
                        >
                          {!mine ? (
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, mb: 0.25 }}>
                              {message.sender?.displayName}
                            </Typography>
                          ) : null}
                          {quotedMessage ? (
                            <Box
                              sx={{
                                mb: 0.75,
                                px: 1,
                                py: 0.75,
                                borderRadius: 1.5,
                                borderLeft: '3px solid',
                                borderColor: mine ? 'secondary.main' : 'primary.main',
                                bgcolor: mine ? 'rgba(255, 250, 239, 0.16)' : 'rgba(15, 76, 69, 0.08)',
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', fontWeight: 900, color: mine ? 'secondary.light' : 'primary.main' }}
                              >
                                {quotedMessage.sender?.displayName || t('chat.reply.unknownSender')}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', color: mine ? 'rgba(255, 250, 239, 0.82)' : 'text.secondary' }}
                              >
                                {quotedMessage.bodyPreview}
                              </Typography>
                            </Box>
                          ) : null}
                          {imageAttachment ? (
                          <Box
                            component="img"
                            src={imageAttachment.url}
                            alt={imageAttachment.fileName || t('chat.attachments.photoMessage')}
                            sx={{
                              display: 'block',
                              width: 'min(260px, 100%)',
                              maxHeight: 320,
                              objectFit: 'cover',
                              borderRadius: 2,
                            }}
                          />
                          ) : isGifUrl(message.body) ? (
                          <Box
                            component="img"
                            src={message.body}
                            alt={t('chat.gif.alt')}
                            sx={{
                              display: 'block',
                              width: 'min(240px, 100%)',
                              borderRadius: 2,
                            }}
                          />
                          ) : (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                              {message.body}
                            </Typography>
                          )}
                        </Box>
                        {message.reactions?.length ? (
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                            {message.reactions.map((reaction) => (
                              <Button
                                type="button"
                                key={reaction.emoji}
                                size="small"
                                onClick={(event) => openReactionPicker(event, message)}
                                sx={{
                                  minWidth: 0,
                                  height: 26,
                                  px: 0.85,
                                  borderRadius: 999,
                                  bgcolor: reaction.viewerReacted
                                    ? 'rgba(236, 170, 65, 0.24)'
                                    : 'rgba(15, 76, 69, 0.08)',
                                  color: 'text.primary',
                                  fontWeight: 900,
                                  lineHeight: 1,
                                }}
                              >
                                <Box component="span" sx={{ mr: 0.45 }}>{reaction.emoji}</Box>
                                {reaction.count}
                              </Button>
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                      {!mine ? (
                        <Stack direction="row" spacing={0.25} sx={{ alignSelf: 'center' }}>
                          <IconButton
                            type="button"
                            aria-label={t('chat.reactions.add')}
                            size="small"
                            onClick={(event) => openReactionPicker(event, message)}
                            sx={{
                              color: 'text.secondary',
                              opacity: 0.72,
                              '&:hover': { opacity: 1 },
                            }}
                          >
                            <EmojiEmotionsOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            type="button"
                            aria-label={t('chat.reply.action')}
                            size="small"
                            onClick={() => startReplyToMessage(message)}
                            sx={{
                              color: 'text.secondary',
                              opacity: 0.72,
                              '&:hover': { opacity: 1 },
                            }}
                          >
                            <ReplyRoundedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : null}
                    </Stack>
                    </Box>
                  );
                })}
                <Box ref={messagesEndRef} />
              </Stack>
            )}
          </Stack>
        </Box>

        <Box
          component="form"
          onSubmit={submitMessage}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(255, 250, 239, 0.84)',
          }}
        >
          <Popover
            open={Boolean(reactionAnchor && reactionMessage)}
            anchorEl={reactionAnchor}
            onClose={closeReactionPicker}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            PaperProps={{
              sx: {
                borderRadius: 999,
                bgcolor: 'rgba(25, 31, 29, 0.96)',
                color: '#fff8e8',
                boxShadow: '0 18px 40px -22px rgba(0, 0, 0, 0.72)',
                px: 0.75,
                py: 0.5,
              },
            }}
          >
            <Stack direction="row" spacing={0.25} alignItems="center">
              {quickReactionEmojis.map((emoji) => (
                <IconButton
                  type="button"
                  key={emoji}
                  onClick={() => reactToMessage(emoji)}
                  sx={{
                    width: 38,
                    height: 38,
                    fontSize: 22,
                    color: 'inherit',
                    '&:hover': { bgcolor: 'rgba(255, 248, 232, 0.14)' },
                  }}
                >
                  {emoji}
                </IconButton>
              ))}
            </Stack>
          </Popover>

          <Popover
            open={Boolean(composerAnchor && composerMode)}
            anchorEl={composerAnchor}
            onClose={() => {
              setComposerAnchor(null);
              setComposerMode(null);
            }}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{
              sx: {
                width: { xs: 'calc(100vw - 2rem)', sm: 360 },
                maxWidth: 420,
                maxHeight: 390,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'rgba(15, 76, 69, 0.16)',
                bgcolor: 'background.paper',
                overflow: 'hidden',
              },
            }}
          >
            {composerMode === 'gif' ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 900 }}>
                  {t('chat.gif.title')}
                </Typography>
                {giphyApiKey ? (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      value={gifQuery}
                      onChange={(event) => setGifQuery(event.target.value)}
                      placeholder={t('chat.gif.search')}
                      sx={{ mt: 1 }}
                    />
                    {gifLoading ? (
                      <Stack alignItems="center" sx={{ py: 3 }}>
                        <CircularProgress size={22} />
                      </Stack>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 1.5 }}>
                        {gifs.map((gif) => (
                          <Box
                            component="button"
                            key={gif.id}
                            type="button"
                            onClick={() => sendBody(gif.images?.fixed_width?.url || gif.url)}
                            sx={{
                              border: 0,
                              p: 0,
                              cursor: 'pointer',
                              bgcolor: 'transparent',
                              borderRadius: 1.5,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              component="img"
                              src={gif.images?.fixed_width?.url}
                              alt={gif.title || t('chat.gif.alt')}
                              sx={{ display: 'block', width: '100%', aspectRatio: '1 / 1', objectFit: 'cover' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    {t('chat.gif.missingKey')}
                  </Typography>
                )}
              </Box>
            ) : null}

            {composerMode === 'add' ? (
              <Box sx={{ py: 1 }}>
                <MenuItem onClick={() => openFilePicker(documentInputRef)}>
                  <DescriptionOutlinedIcon sx={{ mr: 1.5 }} /> {t('chat.attachments.document')}
                </MenuItem>
                <MenuItem onClick={() => openFilePicker(photoInputRef)}>
                  <ImageOutlinedIcon sx={{ mr: 1.5 }} /> {t('chat.attachments.photo')}
                </MenuItem>
              </Box>
            ) : null}
          </Popover>

          {replyToMessage ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                px: 1.25,
                py: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'rgba(15, 76, 69, 0.16)',
                bgcolor: 'rgba(15, 76, 69, 0.06)',
              }}
            >
              <Box
                sx={{
                  width: 3,
                  alignSelf: 'stretch',
                  borderRadius: 999,
                  bgcolor: 'primary.main',
                  flex: '0 0 auto',
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 900 }}>
                  {t('chat.reply.replyingTo', {
                    sender: replyToMessage.sender?.displayName || t('chat.reply.unknownSender'),
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {replyToMessage.bodyPreview}
                </Typography>
              </Box>
              <IconButton
                type="button"
                aria-label={t('chat.reply.cancel')}
                size="small"
                onClick={() => setReplyToMessage(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null}

          <Box
            component="input"
            ref={documentInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.csv,.ics,.zip,.json,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,text/calendar,application/zip,application/json"
            multiple
            onChange={(event) => {
              appendAttachmentDraft(t('chat.attachments.documentTemplate'), event.target.files);
              event.target.value = '';
            }}
            sx={{ display: 'none' }}
          />
          <Box
            component="input"
            ref={photoInputRef}
            type="file"
            accept="image/*,video/*"
            onChange={(event) => {
              sendImageFile(event.target.files?.[0]);
              event.target.value = '';
            }}
            sx={{ display: 'none' }}
          />
          <Box
            component="input"
            ref={cameraPhotoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(event) => {
              sendImageFile(event.target.files?.[0]);
              event.target.value = '';
            }}
            sx={{ display: 'none' }}
          />

          <TextField
            disabled={loading || !conversation?.id}
            fullWidth
            placeholder={t('chat.inputPlaceholder')}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submitMessage(event);
              }
            }}
            inputProps={{ maxLength: 1200 }}
            multiline
            maxRows={4}
            sx={{
              '& .MuiOutlinedInput-root': {
                alignItems: 'center',
                pr: 0.75,
              },
              '& .MuiInputAdornment-positionEnd': {
                ml: 0.5,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Stack direction="row" spacing={0.25}>
                    <IconButton
                      type="button"
                      disabled={loading || !conversation?.id}
                      onClick={(event) => openComposerTool(event, 'add')}
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                    <IconButton
                      type="button"
                      aria-label={t('chat.attachments.camera')}
                      disabled={loading || !conversation?.id}
                      onClick={() => openFilePicker(cameraPhotoInputRef)}
                      size="small"
                    >
                      <CameraAltOutlinedIcon />
                    </IconButton>
                    <IconButton
                      type="button"
                      disabled={loading || !conversation?.id}
                      onClick={(event) => openComposerTool(event, 'gif')}
                      size="small"
                    >
                      <Typography sx={{ fontWeight: 900, fontSize: 12 }}>GIF</Typography>
                    </IconButton>
                  </Stack>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('chat.send')}
                    disabled={!canSend}
                    type="submit"
                    size="small"
                    sx={{
                      width: 32,
                      height: 32,
                      flex: '0 0 32px',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '&.Mui-disabled': {
                        bgcolor: 'rgba(15, 76, 69, 0.12)',
                        color: 'rgba(15, 76, 69, 0.36)',
                      },
                    }}
                  >
                    {sending ? <CircularProgress color="inherit" size={14} /> : <SendRoundedIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Stack>
    </Drawer>
  );
};

export default TavernChatPanel;
